import { ArrowLeft, Printer } from "@phosphor-icons/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import { useAdminHousehold } from "@/lib/api";
import { logger } from "@/lib/logger";

import { buildSheet } from "./sheet-data";
import { SheetStack } from "./sheet-stack";

/**
 * The printable kit sheet for one household.
 *
 * Its own route rather than a hidden block on the detail page, so
 * `window.print()` prints the sheet and nothing else. No AdminShell: no nav, no
 * header, nothing that would land on paper. The on-screen framing (backdrop,
 * gap, shadow, the toolbar below) is stripped in `sheet.css`'s print block.
 *
 * `?print=1` prints as soon as the page is ready, which is how the dialog opens
 * it. Without the flag it is a preview the admin can read first.
 */
export function KitSheetPage() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const { data: household, isLoading, isError } = useAdminHousehold(id ?? null);

  const autoPrint = params.get("print") === "1";
  const printed = useRef(false);
  const [ready, setReady] = useState(false);

  // Memoised because two effects below key off it. Rebuilt every render, it made
  // them re-run every render: the title effect then captured its OWN title as
  // `previous`, so leaving the page left `document.title` as the sheet filename
  // instead of restoring the admin's, and the asset gate re-awaited fonts and
  // re-attached image listeners on every pass.
  const sheet = useMemo(
    () => (household ? buildSheet(household, window.location.origin) : null),
    [household],
  );

  // The document title seeds the print dialog's filename, so it has to be set
  // before the dialog opens. Household name, never the resident's: this PDF
  // gets mailed to whoever prints the kit inserts.
  useEffect(() => {
    if (!sheet) {
      return;
    }
    const previous = document.title;
    document.title = sheet.documentTitle;
    return () => {
      document.title = previous;
    };
  }, [sheet]);

  // Marks the html element so the print-colour rule applies while this page is
  // mounted, rather than to every admin screen someone might print.
  useEffect(() => {
    document.documentElement.classList.add("kit-sheet-print");
    return () => document.documentElement.classList.remove("kit-sheet-print");
  }, []);

  /*
   * Wait for the webfont and the product shots before printing.
   *
   * Printing early is the one failure here that nobody catches: the sheet falls
   * back to Arial, or a product shot prints as a gap, and it looks plausible
   * enough that it only becomes obvious once a box has shipped with it. Both
   * conditions are cheap to wait on, so we wait.
   */
  useEffect(() => {
    if (!sheet) {
      return;
    }
    // A different sheet means different assets, so the gate reopens: `ready`
    // must not stay true from the previous household while new shots load, and
    // the auto-print latch has to release so the new sheet can print itself.
    setReady(false);
    printed.current = false;

    let cancelled = false;
    const images = Array.from(document.querySelectorAll<HTMLImageElement>("img.kit-sheet-shot"));
    const settled = Promise.all([
      document.fonts.ready,
      ...images.map(
        (img) =>
          img.complete ||
          new Promise<void>((resolve) => {
            img.addEventListener("load", () => resolve(), { once: true });
            // A missing shot must not stall the sheet forever. Print without it
            // and let the admin see the gap in the preview.
            img.addEventListener("error", () => resolve(), { once: true });
          }),
      ),
    ]);
    void settled.then(() => {
      if (!cancelled) {
        setReady(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [sheet]);

  /*
   * A sheet with no access code has nothing to scan and no code to type, so it
   * is paper with a hole in it. The dialog already refuses to print one, but the
   * route is reachable directly — a bookmarked URL, a hand-typed `?print=1` —
   * and the guard has to live where the printing happens, not only on the way
   * in. `adminCreateHousehold` always issues a code, so this is mostly imports
   * and hand-edited rows, which is exactly the case nobody is watching for.
   */
  const canPrint = ready && sheet !== null && sheet.code !== null;

  useEffect(() => {
    if (!canPrint || !autoPrint || printed.current) {
      return;
    }
    printed.current = true;
    logger.debug("[kit-sheet] fonts and images settled, opening print dialog");
    window.print();
  }, [autoPrint, canPrint]);

  if (isLoading) {
    return <Centered>Loading the household…</Centered>;
  }
  if (isError || !household || !sheet) {
    return <Centered>That household could not be loaded.</Centered>;
  }

  return (
    <div className="bg-surface-strong min-h-screen">
      <SheetStack sheet={sheet} />

      {/* Screen-only toolbar. `print:hidden` keeps it off the paper. */}
      <div className="flex justify-center gap-2.5 pb-10 print:hidden">
        <Button asChild variant="outline">
          <Link to={`/admin/households/${household.id}`}>
            <ArrowLeft className="size-4" weight="light" />
            Back to household
          </Link>
        </Button>
        <Button type="button" disabled={!canPrint} onClick={() => window.print()}>
          <Printer className="size-4" weight="light" />
          {printLabel(ready, sheet.code !== null)}
        </Button>
      </div>
      {sheet.code === null && (
        <p role="status" className="text-destructive pb-10 text-center text-[13px] print:hidden">
          This home has no access code, so there is nothing to scan. Issue one from the household
          page, then come back.
        </p>
      )}
    </div>
  );
}

/** Why the print button is off, said on the button itself. */
function printLabel(ready: boolean, hasCode: boolean): string {
  if (!hasCode) {
    return "No access code";
  }
  return ready ? "Print" : "Preparing…";
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-muted grid min-h-screen place-items-center px-6 text-center text-[15px]">
      {children}
    </div>
  );
}
